import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { IClientFloors, IClientPlaces, IFloorProp, IPropType, ITables } from '../../all-types';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { updateFloor, updatePlace, updateRestaurantTable } from '../../services';
import { PlaceArea } from './adjustable-place';
import { AdjustableProp } from './adjustable-props';
import { DraggableTable } from './adjustable-table';
import { CommonBtn } from '../../components';

const FloorDesigner: React.FC = () => {
    const dispatch = useAppDispatch();
    const allFloors = useAppSelector(state => state.preloadData.floors);
    const allPlaces = useAppSelector(state => state.preloadData.places);
    const allTables = useAppSelector(state => state.preloadData.tables);
    const allProps = useAppSelector(state => state.props.props);

    const [selectedFloor, setSelectedFloor] = useState<IClientFloors>(allFloors[0]);
    const [placesOnFloor, setPlacesOnFloor] = useState<IClientPlaces[]>([]);
    const [tablesOnFloor, setTablesOnFloor] = useState<ITables[]>([]);
    const [addPropDialogOpen, setAddPropDialogOpen] = useState(false);
    const [selectedProp, setSelectedProp] = useState<IPropType | null>(null);

    // Initialize with first floor if available
    // useEffect(() => {
    //     if (allFloors.length > 0) {
    //         setSelectedFloor(allFloors[0]);
    //     }
    // }, [allFloors]);

    // Filter places and tables for the selected floor
    useEffect(() => {
        if (selectedFloor) {
            // Get places for this floor
            const floorPlaces = allPlaces.filter(place => place.floorId === selectedFloor._id);
            setPlacesOnFloor(floorPlaces);

            // Get tables for this floor
            const floorTables = allTables.filter(table => table.floorId === selectedFloor._id);
            setTablesOnFloor(floorTables);

        }
    }, [selectedFloor, allPlaces, allTables]);

    const handleUpdateTablePosition = async (table: ITables, newX: number, newY: number, w: number, h: number, r: number) => {
        try {
            const updatedTable = { ...table, xAxis: newX, yAxis: newY, width: w, height: h, rotation: r };
            setTablesOnFloor(prev =>
                prev.map(t => t._id === table._id ? updatedTable : t)
            );

            if (table._id) {
                dispatch(updateRestaurantTable({
                    id: table._id,
                    table: updatedTable
                }));
            }
        } catch (e) {
            console.error('Error updating table position:', e);
        }
    };

    const handleUpdatePlacePosition = async (place: IClientPlaces, newX: number, newY: number, width: number, height: number) => {
        try {
            const newPlace = { ...place, placeMap: { ...place.placeMap, xAxis: newX, yAxis: newY, width, height } };
            setPlacesOnFloor(prev =>
                prev.map(p => p._id === place._id ? newPlace : p)
            );
            if (place._id) {
                await dispatch(updatePlace({ id: place._id, place: newPlace }));
            }
        } catch (e) {
            console.error('Error updating place position:', e);
        }
    };

    if (!selectedFloor) {
        return <Typography>Loading floor data...</Typography>;
    }

    if (!selectedFloor.floorMap) {
        return <Typography>No floor map data available.</Typography>;
    }

    return (
        <Box sx={{
            //p: 3, 
            display: 'flex', flexDirection: 'column', //alignItems: 'center'
        }}>
            <Stack>
            {/* <Typography variant="h5" gutterBottom>
                Floor Plan Designer - Floor {selectedFloor.floorNo}
            </Typography> */}
            <CommonBtn
                variant="outlined"
                color="secondary"
                onClick={() => setAddPropDialogOpen(true)}
                sx={{ ml: 2 }}
            >
                Add Props
            </CommonBtn>
            </Stack>
          
            {/* Floor selector dropdown would go here */}

            <Paper
                elevation={4}
                sx={{
                    position: 'relative',
                    width: selectedFloor.floorMap.width,
                    height: selectedFloor.floorMap.height,
                    backgroundImage: `url(${selectedFloor.floorMap.bgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    overflow: 'hidden',
                    //border: '2px solid #3f51b5',
                    //borderRadius: '8px',
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                }}
            >
                {tablesOnFloor.map(table => {
                    return (
                        <DraggableTable
                            key={table._id}
                            table={table}
                            onUpdatePosition={handleUpdateTablePosition}
                        />
                    );
                })}

                {placesOnFloor.map((plc, index) => {
                    return (
                        <PlaceArea
                            key={plc._id || index}
                            place={plc}
                            onUpdatePosition={handleUpdatePlacePosition}
                        />
                    );
                })}

                {selectedFloor.floorMap.floorProps.map((fp, index) => (
                    <AdjustableProp
                        key={`prop-${index}-${fp.propName}`}
                        index={index}
                        prop={fp}
                        onUpdate={(i, updated) => {
                            const updatedProps = [...selectedFloor.floorMap.floorProps];
                            updatedProps[i] = updated;
                            const updatedFloor = {
                                ...selectedFloor,
                                floorMap: { ...selectedFloor.floorMap, floorProps: updatedProps },
                            };
                            setSelectedFloor(updatedFloor);
                            if (selectedFloor._id) {
                                dispatch(updateFloor({ id: selectedFloor._id, floor: updatedFloor }));
                            }
                        }}
                        onDelete={(i) => {
                            const updatedProps = [...selectedFloor.floorMap.floorProps].filter((_, idx) => idx !== i);

                            const updatedFloor = {
                                ...selectedFloor,
                                floorMap: {
                                    ...selectedFloor.floorMap,
                                    floorProps: updatedProps,
                                },
                            };

                            setSelectedFloor(updatedFloor);

                            if (selectedFloor._id) {
                                dispatch(updateFloor({ id: selectedFloor._id, floor: updatedFloor }))
                                    .unwrap()
                                    .then(() => {
                                        // optionally refetch or reset state after backend responds
                                        console.log('Delete successful');
                                    })
                                    .catch((err) => {
                                        console.error('Delete failed:', err);
                                        // optionally rollback here if needed
                                    });
                            }
                        }}
                        onDuplicate={(i) => {
                            const original:IFloorProp = selectedFloor.floorMap.floorProps[i];
                            const copy = {
                                ...original,
                                propName: `${original.propName}-${new Date().getTime()}`,
                                xAxis: original.xAxis + 20,
                                yAxis: original.yAxis + 20,
                            };
                            const updatedProps = [...selectedFloor.floorMap.floorProps, copy];
                            const updatedFloor = {
                                ...selectedFloor,
                                floorMap: { ...selectedFloor.floorMap, floorProps: updatedProps },
                            };
                            setSelectedFloor(updatedFloor);
                            if (selectedFloor._id) {
                                dispatch(updateFloor({ id: selectedFloor._id, floor: updatedFloor }));
                            }
                        }}
                    />
                ))}
            </Paper>

            {/* <Box mt={2}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => console.log('Save Floor Design', { selectedFloor, tablesOnFloor })}
                >
                    Save Floor Layout
                </Button>
            </Box> */}

            <Dialog open={addPropDialogOpen} onClose={() => setAddPropDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Select a Prop</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        {allProps.map((prop, index) => (
                            <Grid
                                item
                                key={index}
                                xs={4}
                                onClick={() => setSelectedProp(prop)}
                                sx={{
                                    //border: selectedProp?.propName === prop.propName ? '2px solid #3f51b5' : '1px solid #ccc',
                                    borderRadius: '8px',
                                    p: 0.5,
                                    cursor: 'pointer',
                                    textAlign: 'center'
                                }}
                            >
                                <img src={prop.propUrl} alt={prop.propName} width={60} height={60} />
                                {/* <Typography variant="caption">{prop.propName}</Typography> */}
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddPropDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (selectedProp && selectedFloor) {
                                const newFloorProp = {
                                    propName: `${selectedProp.propName}-${new Date().getTime()}`,
                                    propImage: selectedProp.propUrl,
                                    propIcon: "",
                                    width: 50,
                                    height: 50,
                                    xAxis: 100,
                                    yAxis: 100,
                                    rotation: 0,
                                    isPlace: false,
                                    placeId: '',
                                };

                                const updatedFloor = {
                                    ...selectedFloor,
                                    floorMap: {
                                        ...selectedFloor.floorMap,
                                        floorProps: [...selectedFloor.floorMap.floorProps, newFloorProp],
                                    },
                                };

                                setSelectedFloor(updatedFloor);
                                setSelectedProp(null);
                                setAddPropDialogOpen(false);
                            }
                        }}
                        disabled={!selectedProp}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* <FloorViewer  floorId={selectedFloor._id || ""}/> */}
        </Box>
    );
};

export { FloorDesigner };
